// Shader presets: named snapshots of one shader's params, kept per user across projects in `UserData.settings.source_presets` — `{ [shaderSource]: [{ id, name, values, thumbnail? }] }` (orc adds `thumbnailUrl` on read). Loading one fades into it like a source switch.
import { diff } from 'deep-object-diff'
import { useSyncExternalStore } from 'react'
import { useSelector } from 'react-redux'
import { v4 as uuid } from 'uuid'
import { CONTEXTS, EMPTY_ARRAY, EMPTY_OBJECT } from '~/lib/constants'
import { parseLivePath, sourceParams, sourceValues, vocabPinId, vocabPinValues } from '~/lib/params'
import { pinValue } from '~/lib/pinLayer'
import { getShader, isShaderSource } from '~/lib/shaders'
import { fillSourcesOf } from '~/lib/sourceFill'
import { drawPresetThumb, showLocalThumb } from '~/lib/sourcePresetThumbs'
import { pickSource, setSourceSettings } from '~/lib/videoSource'
import api, { useGetUserQuery } from '~/store/api'
import { selectCameras } from '~/store/camera/selectors'
import { registerAction } from '~/store/middleware/confirmActions'
import { selectLiveProps } from '~/store/playback/selectors'
import { requestConfirm, requestPrompt, setPanelCollapsed } from '~/store/ui'

const logger = console.scope('sourcePresets')

export const hasSourcePresets = isShaderSource

export const sameValues = (a, b) => !Object.keys(diff(a ?? EMPTY_OBJECT, b ?? EMPTY_OBJECT)).length

/** The `ui.panels.collapsed` key of a source's presets in the sources browser; folded unless set false. */
export const presetsCollapseKey = source => `${CONTEXTS.SOURCE}:presets:${source}`

// Built on first use: `~/store/api` is still unevaluated when an import cycle loads this module.
let selectUser
const selectAllSourcePresets = state => {
	selectUser ??= api.endpoints.getUser.select()
	return selectUser(state).data?.settings?.source_presets ?? EMPTY_OBJECT
}

/** Every source's presets; subscribes to the user doc. */
export const useSourcePresets = () => {
	useGetUserQuery()
	return useSelector(selectAllSourcePresets)
}

/** A source's bag in this preset, sparse, as a preset saves it. */
const liveValues = (state, source) => sourceValues(state.live, source)

/** `values` with each pinned param of `source` held at its current value, as a preset load holds it. */
const keepPinned = (state, source, values) => {
	const params = sourceParams(source)
	const live = liveValues(state, source)
	const { pins } = state.playback
	let out = values
	for (const key of selectLiveProps(state)) {
		const id = vocabPinId(key)
		const p = id ? null : parseLivePath(key)
		const k = id ?? (p?.scope === 'source' && p.source === source ? p.key : null)
		if (!k || !params?.[k]) continue
		const v = live[k] ?? (id ? vocabPinValues(pins)?.[k] : pinValue(pins, key))
		out = { ...out }
		if (v === undefined) delete out[k]
		else out[k] = v
	}
	return out
}

export const useLiveValues = source => useSelector(state => liveValues(state, source), sameValues)

// A fill in this preset shows it.
const isLive = (state, source) => fillSourcesOf(state.live.objects).has(source)

// The preset last loaded or saved per source, for this tab only: what Save overwrites.
let loaded = EMPTY_OBJECT
const listeners = new Set()
const markLoaded = (source, id) => {
	loaded = { ...loaded, [source]: id }
	for (const fn of listeners) fn()
}
const subscribe = fn => {
	listeners.add(fn)
	return () => listeners.delete(fn)
}

/** The source's own defaults, loadable like a preset: its row in the browser is the source itself. */
export const DEFAULT_PRESET = 'default'

/** `{ preset, modified }` for a source's current values: the preset last loaded if it still exists, else the first whose values match; null when none applies or the defaults were loaded last. */
export const useActivePreset = (source, presets, values) =>
	matchPreset(
		useSyncExternalStore(subscribe, () => loaded[source]),
		presets,
		values
	)

/** `useActivePreset` outside React (the console probe). */
export const activeSourcePreset = (state, source, values) =>
	matchPreset(loaded[source], selectAllSourcePresets(state)[source] ?? EMPTY_ARRAY, values)

const matchPreset = (loadedId, presets, values) => {
	if (loadedId === DEFAULT_PRESET) return null
	const last = presets.find(p => p.id === loadedId)
	if (last) return { preset: last, modified: !sameValues(last.values, values) }
	const match = presets.find(p => sameValues(p.values, values))
	return match ? { preset: match, modified: false } : null
}

/** Read-modify-write of one source's list; the whole settings bag goes back, as the endpoint replaces it. */
const writePresets = (source, update) => async (dispatch, getState) => {
	await dispatch(api.endpoints.getUser.initiate(undefined, { subscribe: false }))
	const user = api.endpoints.getUser.select()(getState()).data
	if (!user) return logger.warn('no user doc; presets not saved')
	const settings = user.settings ?? EMPTY_OBJECT
	const all = { ...settings.source_presets }
	const list = update(all[source] ?? EMPTY_ARRAY)
	if (list.length) all[source] = list
	else delete all[source]
	await dispatch(api.endpoints.updateUserSettings.initiate({ ...settings, source_presets: all }))
}

const nextName = list => `Preset ${list.length + 1}`

/** Draws, shows and uploads one preset's thumb, then puts its key on the preset; a failure leaves the preset's last thumb. */
const updatePresetThumb = (source, preset) => async dispatch => {
	try {
		const blob = await drawPresetThumb(source, preset.values)
		if (!blob) return
		showLocalThumb(preset.id, blob)
		const { thumbnail } = await dispatch(
			api.endpoints.uploadSourcePresetThumb.initiate({ id: preset.id, blob })
		).unwrap()
		await dispatch(writePresets(source, l => l.map(p => (p.id === preset.id ? { ...p, thumbnail } : p))))
	} catch (e) {
		logger.warn('preset thumb failed', source, preset.id, e)
	}
}

/** Redraws the thumbs of `source`'s presets, or every source's: a dev-time step after the shader's look changes. */
const regenPresetThumbs = source => async (dispatch, getState) => {
	const all = selectAllSourcePresets(getState())
	for (const [s, list] of Object.entries(all))
		if (!source || s === source) for (const p of list) await dispatch(updatePresetThumb(s, p))
}

globalThis.__regenPresetThumbs = source => globalThis.store?.dispatch(regenPresetThumbs(source))

/** Saves the live values under a name: an existing preset of that name takes them, any other name adds one. */
export const saveSourcePreset =
	({ source, inputValue }) =>
	(dispatch, getState) => {
		const state = getState()
		if (!isLive(state, source)) return
		const list = selectAllSourcePresets(state)[source] ?? EMPTY_ARRAY
		const name = inputValue?.trim() || nextName(list)
		const values = liveValues(state, source)
		const existing = list.find(p => p.name === name)
		const preset = existing ? { ...existing, values } : { id: uuid(), name, values }
		markLoaded(source, preset.id)
		dispatch(setPanelCollapsed({ id: presetsCollapseKey(source), collapsed: false }))
		const written = dispatch(
			writePresets(source, l => (existing ? l.map(p => (p.id === preset.id ? preset : p)) : [...l, preset]))
		)
		written.then(() => dispatch(updatePresetThumb(source, preset)))
		return written
	}

/** `{ source, preset }` for a preset id, whichever source holds it; null when none does. */
export const findSourcePreset = (state, id) => {
	for (const [source, list] of Object.entries(selectAllSourcePresets(state))) {
		const preset = list.find(p => p.id === id)
		if (preset) return { source, preset }
	}
	return null
}

/** `RenameField`'s writer for a `source_preset`. */
export const renameSourcePreset =
	({ id, name }) =>
	(dispatch, getState) => {
		const source = findSourcePreset(getState(), id)?.source
		if (source) return dispatch(writePresets(source, l => l.map(p => (p.id === id ? { ...p, name } : p))))
	}

export const deleteSourcePreset = ({ source, id }) => writePresets(source, l => l.filter(p => p.id !== id))

/** Puts a preset (or `DEFAULT_PRESET`) on stage: a shader some fill shows fades its bag in, any other fills the target with the preset's values (`pickSource`). */
export const loadSourcePreset =
	({ source, id, cameras, instant }) =>
	(dispatch, getState) => {
		const state = getState()
		const saved =
			id === DEFAULT_PRESET ? EMPTY_OBJECT : selectAllSourcePresets(state)[source]?.find(p => p.id === id)?.values
		if (!saved) return
		const values = keepPinned(state, source, saved)
		markLoaded(source, id)
		if (isLive(state, source)) setSourceSettings(dispatch, source, values, { instant })
		else pickSource(dispatch, source, cameras, { settings: values, instant })
	}

/** A sources-browser item picked in `mode` (`pickSource`); a preset item brings its shader with the preset's values. */
export const pickSourceItem =
	({ id, mode }) =>
	(dispatch, getState) => {
		if (!id) return
		const state = getState()
		const found = findSourcePreset(state, id)
		if (found) markLoaded(found.source, id)
		return found
			? pickSource(dispatch, found.source, selectCameras(state), {
					settings: keepPinned(state, found.source, found.preset.values),
					mode,
				})
			: pickSource(dispatch, id, selectCameras(state), { mode })
	}

registerAction('saveSourcePreset', saveSourcePreset)
registerAction('deleteSourcePreset', deleteSourcePreset)

/** Asks for a name, offering the active preset's, so Enter updates it. */
export const promptSavePreset = (source, presets, active) =>
	requestPrompt({
		title: `Save ${getShader(source)?.label ?? 'shader'} preset`,
		// message: presets.length ? 'An existing name updates that preset.' : undefined,
		inputValue: active?.name ?? nextName(presets),
		inputPlaceholder: 'Preset name',
		action: { type: 'saveSourcePreset', params: { source } },
	})

export const confirmDeletePreset = (source, preset) =>
	requestConfirm({
		title: 'Delete Preset',
		message: `Delete "${preset.name}"?`,
		destructive: true,
		action: { type: 'deleteSourcePreset', params: { source, id: preset.id } },
	})
